---
title: "Video transcript API: one call for TikTok, YouTube, Instagram"
description: "One API for video transcripts across TikTok, YouTube, and Instagram. Same request shape, plain or timestamped text, paid per call, no per-platform glue."
date: "2026-06-23"
author: "Kevin Wang"
tags: ["transcripts", "api", "how-to", "video"]
keyword: "video transcript api"
ogImage: "/og/video-transcript-api.png"
---

export const transcriptActors = ["Your code", "AnyAPI"];

export const transcriptFlow = [
  { from: "Your code", to: "AnyAPI", label: "POST any video URL" },
  { from: "AnyAPI", label: "route + normalize the response", highlight: true },
  { from: "AnyAPI", to: "Your code", label: "200 + transcript under output.data" },
];

<Callout type="tip" title="Key findings">

One API gives you video transcripts across TikTok, YouTube, and Instagram. It's the same request shape every time, and the transcript always comes back under `output.data`. It's <Price sku="tiktok.video_transcript" per="call" /> a call on TikTok, <Price sku="instagram.media_transcript" per="call" /> on Instagram, and <Price sku="youtube.video_transcript" per="call" /> on YouTube, with no per-platform glue.

</Callout>

Three platforms, three different walls. YouTube hides its captions behind OAuth, Instagram exposes none at all, and TikTok has no official API.

Build it yourself and you maintain three scrapers, three auth schemes, and three output shapes.

That's three integrations to write and keep alive, for one feature: a transcript.

One request shape returns the spoken text from **any of the three, under one envelope.** The rest of this page walks through it.

## One shape, every platform

The call is the same on all three. You pass a URL, and the transcript comes back under `output.data`. Here's the call I actually run, using YouTube as the example:

<CodeTabs labels={["cURL", "Python", "JavaScript"]}>

```bash
curl -X POST https://api.getanyapi.com/v1/run/youtube.video_transcript \
  -H "Authorization: Bearer $ANYAPI_KEY" \
  -H "Content-Type: application/json" \
  -d '{"url": "https://www.youtube.com/watch?v=dQw4w9WgXcQ"}'
```

```python
import os, requests

res = requests.post(
    "https://api.getanyapi.com/v1/run/youtube.video_transcript",
    headers={"Authorization": f"Bearer {os.environ['ANYAPI_KEY']}"},
    json={"url": "https://www.youtube.com/watch?v=dQw4w9WgXcQ"},
)
print(res.json()["output"]["data"]["transcript"])
```

```js
const res = await fetch("https://api.getanyapi.com/v1/run/youtube.video_transcript", {
  method: "POST",
  headers: {
    Authorization: `Bearer ${process.env.ANYAPI_KEY}`,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({ url: "https://www.youtube.com/watch?v=dQw4w9WgXcQ" }),
});
const { output } = await res.json();
console.log(output.data.transcript);
```

</CodeTabs>

You change only the SKU per platform: `tiktok.video_transcript` for TikTok, `instagram.media_transcript` for Instagram. Everything else stays the same.

**One key, one auth header, one envelope across all three.** No Google Cloud project for YouTube, no Graph API for Instagram, no headless browser for TikTok.

<Chart caption="The same one-call pattern on every platform. The gateway routes the URL to the right source and normalizes the response, so your code reads one shape.">
  <SequenceDiagram actors={transcriptActors} rows={transcriptFlow} caption="One request in, a transcript out under output.data, whichever platform the URL points to." ariaLabel="Your code POSTs any video URL to AnyAPI, which routes and normalizes it, then returns a transcript under output.data." />
</Chart>

## What each platform gives you

The calls match; the platforms don't. How much each one exposes decides what you get back, and it's worth knowing before you build:

| | TikTok | YouTube | Instagram |
| --- | --- | --- | --- |
| Source of the text | caption track | caption track | transcribed audio |
| Output shape | WebVTT | timed line array | plain text |
| Timestamps | yes | yes | no |
| Language tag | sometimes | yes | no |
| Price per call | <Price sku="tiktok.video_transcript" per="call" /> | <Price sku="youtube.video_transcript" per="call" /> | <Price sku="instagram.media_transcript" per="call" /> |

TikTok and YouTube publish a caption track, so you get timestamps for free. Instagram publishes none, so the audio is transcribed and you get clean text without offsets.

Price barely moves between them: <Price sku="tiktok.video_transcript" /> per 1,000 transcripts on TikTok, <Price sku="instagram.media_transcript" /> on Instagram, and <Price sku="youtube.video_transcript" /> on YouTube.

## The deep dive per platform

Each platform has its own gotcha and its own deep dive. Start with the one you need:

- **TikTok.** No official API, so a scraper or a hosted call is the only way. [How to get a TikTok transcript with one API call](/blog/get-tiktok-transcript-api).
- **YouTube.** The official `captions.download` needs OAuth and 403s on videos you don't own, and the popular OSS library breaks on cloud IPs. [YouTube transcript API, explained](/blog/youtube-transcript-api).
- **Instagram.** No transcript resource and no caption track at all, so the audio has to be transcribed. [Instagram transcript API, explained](/blog/instagram-transcript-api).

## Normalize once, branch where it matters

Because the envelope is shared, a small wrapper handles every platform and you branch only on the output shape. Here's the whole seam:

```javascript
async function transcript(sku, url) {
  const res = await fetch(`https://api.getanyapi.com/v1/run/${sku}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${process.env.ANYAPI_KEY}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ url }),
  });
  const { output } = await res.json();
  if (!output.found) return null;            // no transcript available
  // YouTube/TikTok: output.data.transcript ; Instagram: output.data.transcripts[].text
  return output.data.transcript ?? output.data.transcripts?.map((t) => t.text).join("\n\n");
}
```

**Write the fetch once, and adding a platform is just a new SKU string.** That's the point of a single gateway: the surface you maintain doesn't grow with the number of sources.

## Limits worth knowing before you build

One envelope hides the plumbing, not the platforms. Three limits still reach your code:

- **No timestamps on Instagram.** There's no caption track to read, so the audio is transcribed and you get clean text with no offsets. Anything that needs to seek to a moment in the video, like clipping or a subtitle file, works on TikTok and YouTube only.
- **No language selector anywhere.** None of the three endpoints takes a language input, so you get whatever track the platform serves for that video. YouTube always tags what it returned, TikTok tags it when it knows, Instagram doesn't tag it at all.
- **A video you can't watch has no transcript.** Private, deleted, and region-locked URLs come back as `output.found` false with `output.data` null rather than an error, which is why the wrapper checks `found` before it touches the data.

## Try them without writing code

Each platform has a free, no-login tool. Paste a link, copy the text, see the output shape before you wire up the API:

- [TikTok transcript tool](/tools/tiktok-transcript)
- [YouTube transcript tool](/tools/youtube-transcript)
- [Instagram transcript tool](/tools/instagram-transcript)

## Frequently asked questions

**How much does a transcript cost?**

<Price sku="tiktok.video_transcript" per="call" /> per call on TikTok, <Price sku="instagram.media_transcript" per="call" /> on Instagram, and <Price sku="youtube.video_transcript" per="call" /> on YouTube, billed in dollars, with no subscription. Per 1,000 transcripts, that's <Price sku="tiktok.video_transcript" />, <Price sku="instagram.media_transcript" />, and <Price sku="youtube.video_transcript" />.

**Can I get a transcript for free?**

Yes, for a one-off. Each platform has a free in-browser tool (linked above) with no key or login. For bulk or programmatic use, the endpoint is one call per video at the prices above.

**Can I transcribe many videos at once?**

Yes. Loop the endpoint over a list of URLs; each call is independent and priced the same. The per-platform guides show the shell pattern.

**What can I build with it?**

Cross-platform search over a creator's whole footprint, an LLM summarizer or repurposing tool, subtitles, translation, or a RAG index for an assistant, all from one integration.

## Related guides

<RelatedGuides items={[
  { to: "/blog/get-tiktok-transcript-api", title: "TikTok transcript API", blurb: "One call for the spoken transcript of any public TikTok." },
  { to: "/blog/youtube-transcript-api", title: "YouTube transcript API", blurb: "The captions.download trap, and one call for any public video." },
  { to: "/blog/instagram-transcript-api", title: "Instagram transcript API", blurb: "Transcribe any public Reel, where there's no caption track at all." },
]} />

<CTA to="/catalog" note="Video transcripts on three platforms, plus 200+ other data sources, one key, priced in dollars. New accounts start with free credit.">
  Browse the data catalog
</CTA>
